import { FolderOpen, PenLine, Paperclip, AlertTriangle, MessageCircleQuestion, Check } from 'lucide-react';
import { STATUTS } from './constantes';
import { CommentaireGuide } from './CommentaireGuide';

const DATE = /(^|[^\p{L}])(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)(?!\p{L})|\b20\d\d\b|\b\d{1,2}\/\d{1,2}\b/iu;
const Repere = ({ ok, label, testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`} data-testid={testId}>
    {ok && <Check className="h-3 w-3" />}{label}
  </span>
);

const Etiquette = ({ Icone, children, className = '' }) => (
  <p className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${className}`}><Icone className="h-3.5 w-3.5" /> {children}</p>
);

const Preuves = ({ i, prog, onBasculer, lecture }) => (
  <div>
    <Etiquette Icone={Paperclip} className="text-slate-500">Preuves à joindre</Etiquette>
    <ul className="mt-2 space-y-1.5">
      {i.preuves.map((p, k) => {
        const ok = prog.preuves.includes(k);
        return (
          <li key={p}>
            <label className={`flex items-start gap-2.5 text-sm text-slate-700 ${lecture ? '' : 'cursor-pointer'}`} data-testid={`guide-preuve-${i.id}-${k}`}>
              <input type="checkbox" checked={ok} disabled={lecture} onChange={() => onBasculer(k)} className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" />
              <span className={ok ? 'text-slate-400 line-through' : ''}>{p}</span>
            </label>
          </li>
        );
      })}
    </ul>
  </div>
);

const MonTravail = ({ i, prog, maj, lecture }) => {
  const t = prog.exemple;
  return (
    <div className="border-t border-slate-100 bg-slate-50/70 p-4 sm:p-5" data-testid={`guide-travail-${i.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs font-bold uppercase tracking-wider text-slate-500">{lecture ? 'Statut' : 'Je me situe'}</span>
        {STATUTS.map((s) => (
          <button key={s.cle} type="button" disabled={lecture} onClick={() => maj({ statut: s.cle })} data-testid={`guide-statut-${i.id}-${s.cle}`} aria-pressed={prog.statut === s.cle}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-default ${prog.statut === s.cle ? s.actif : 'bg-white text-slate-500 ring-1 ring-slate-200 enabled:hover:bg-slate-100'}`}>
            {s.label}
          </button>
        ))}
      </div>
      <textarea value={t} readOnly={lecture} onChange={(e) => maj({ exemple: e.target.value })} rows={3} data-testid={`guide-mon-exemple-${i.id}`}
        placeholder={lecture ? 'Pas encore d\'exemple rédigé.' : "Mon exemple : ce que j'ai vraiment fait dans ma structure, avec la date, les chiffres et le lieu…"}
        className={`mt-3 w-full resize-y rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none transition-shadow ${lecture ? 'cursor-default bg-slate-100' : 'bg-white focus:ring-2 focus:ring-[#8DC63F]/60'}`} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Repere ok={DATE.test(t)} label="Daté" testId={`guide-repere-date-${i.id}`} />
        <Repere ok={/\d/.test(t)} label="Chiffré" testId={`guide-repere-chiffre-${i.id}`} />
        <Repere ok={t.trim().length >= 150} label="Détaillé" testId={`guide-repere-detail-${i.id}`} />
      </div>
    </div>
  );
};

export const IndicateurGuide = ({ i, k, teinte, progression }) => {
  const prog = progression.ind(i.id);
  return (
    <article className={`overflow-hidden rounded-2xl border border-slate-200 border-l-4 bg-white ${teinte.bord}`} data-testid={`guide-indicateur-${i.id}`}>
      <div className="grid lg:grid-cols-[2fr_3fr]">
        <div className={`space-y-3 p-4 sm:p-5 ${teinte.clair}`}>
          <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold tracking-wide text-white ${teinte.fort}`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25 text-[11px]">{k}</span>{i.verbe}
          </span>
          <p className="text-sm italic leading-relaxed text-slate-500" data-testid={`guide-texte-${i.id}`}>« {i.texte} »</p>
          <div>
            <Etiquette Icone={FolderOpen} className={teinte.texte}>Ce qu'il faut faire</Etiquette>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{i.a_faire}</p>
          </div>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <div className="rounded-xl border-l-4 border-[#E9A23B] bg-[#FFF8E6] p-3.5" data-testid={`guide-exemple-${i.id}`}>
            <Etiquette Icone={PenLine} className="text-[#7A4B00]">Exemple · Cap Océan Glisse</Etiquette>
            <p className="mt-1.5 text-sm italic leading-relaxed text-[#7A4B00]">{i.exemple}</p>
          </div>
          <Preuves i={i} prog={prog} lecture={progression.lecture} onBasculer={(p) => progression.basculerPreuve(i.id, p)} />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-red-50 p-3"><Etiquette Icone={AlertTriangle} className="text-red-700">Piège à éviter</Etiquette><p className="mt-1 text-sm text-red-800">{i.piege}</p></div>
            <div className="rounded-xl bg-blue-50 p-3" data-testid={`guide-jury-${i.id}`}><Etiquette Icone={MessageCircleQuestion} className="text-blue-700">Question du jury</Etiquette><p className="mt-1 text-sm italic text-blue-800">{i.jury}</p></div>
          </div>
        </div>
      </div>
      <MonTravail i={i} prog={prog} lecture={progression.lecture} maj={(patch) => progression.majInd(i.id, patch)} />
      <CommentaireGuide i={i} progression={progression} />
    </article>
  );
};
